import { useUserStore } from '@app/stores/user'

/**
 * Matomo Analytics Plugin
 * Injects Matomo tracking script on client-side and tracks the authenticated
 * user via Matomo User ID.
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const { siteId, siteUrl } = config.public.matomo

  // Don't initialize if no site ID or site URL is configured
  if (!siteId || !siteUrl) {
    console.warn('Matomo: Missing site ID or site URL, skipping initialization')
    return
  }

  // Ensure siteUrl ends with a trailing slash
  const u = siteUrl.endsWith('/') ? siteUrl : siteUrl + '/'

  // Initialize Matomo
  const _paq = (window._paq = window._paq || [])

  _paq.push(['enableLinkTracking'])
  _paq.push(['setTrackerUrl', u + 'matomo.php'])
  _paq.push(['setSiteId', siteId])

  // Load Matomo script
  const d = document
  const g = d.createElement('script')
  const s = d.getElementsByTagName('script')[0]
  g.async = true
  g.src = u + 'matomo.js'
  s.parentNode?.insertBefore(g, s)

  // --- Matomo User ID tracking ---
  // Send a stable, non-PII identifier for the authenticated user. We use the
  // internal user id, SHA-256 hashed + base64 encoded, so the raw id is never
  // transmitted (per DSF requirement). No value is sent for anonymous users.
  const userStore = useUserStore()
  let hashedUserId: string | null = null

  const hashUserId = async (raw: string): Promise<string> => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
  }

  watch(
    () => userStore.user?.id,
    async (id, prevId) => {
      if (id != null) {
        // Authenticated: set the User ID (must precede trackPageView).
        hashedUserId = await hashUserId(String(id))
        _paq.push(['setUserId', hashedUserId])
      } else if (prevId != null) {
        // Logged out: stop attributing actions to the user and start a new visit.
        hashedUserId = null
        _paq.push(['resetUserId'])
        _paq.push(['trackPageView'])
      }
    },
    { immediate: true },
  )

  // Track page views on route change (for SPA navigation)
  const router = useRouter()
  let isFirstNavigation = true
  router.afterEach((to) => {
    nextTick(() => {
      if (isFirstNavigation) {
        // First page view — just track without setCustomUrl to avoid double counting
        isFirstNavigation = false
        _paq.push(['trackPageView'])
        return
      }
      // Re-assert the User ID before every page view for authenticated users.
      if (hashedUserId) _paq.push(['setUserId', hashedUserId])
      _paq.push(['setCustomUrl', to.fullPath])
      _paq.push(['setDocumentTitle', document.title])
      _paq.push(['trackPageView'])
    })
  })
})

// Type declaration for window._paq
declare global {
  interface Window {
    _paq: unknown[][]
  }
}
